import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import styled from 'styled-components';
import {
  USER_TYPES,
  getUserType,
  getHomePathForUser,
  userHasAllowedType,
} from '../utils/userTypeUtils';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: ${props => props.theme.colors.background};
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid ${props => props.theme.colors.border};
  border-top: 3px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/**
 * @param {boolean} [adminOnly] — shorthand for allowedUserTypes=[admin]
 * @param {string[]} [allowedUserTypes] — persona ACL via getUserType
 * @param {string} [redirectTo] — override forbidden redirect
 * @param {boolean} [blockSalesLimited] — send sales_team away from full-admin pages
 */
const ProtectedRoute = ({
  children,
  adminOnly = false,
  allowedUserTypes,
  redirectTo,
  blockSalesLimited = false,
}) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (!isAuthenticated) {
    const loginPath = location.pathname.startsWith('/admin')
      ? '/admin/login'
      : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  const allowed = adminOnly
    ? [USER_TYPES.ADMIN]
    : allowedUserTypes;

  if (allowed && allowed.length > 0 && !userHasAllowedType(user, allowed)) {
    if (adminOnly) {
      return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }
    const fallback = redirectTo || getHomePathForUser(user);
    return <Navigate to={fallback} state={{ from: location, forbidden: true }} replace />;
  }

  // Staff without user_type=admin still pass adminOnly via getUserType
  if (adminOnly && getUserType(user) !== USER_TYPES.ADMIN) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (
    blockSalesLimited &&
    (user?.admin_role === 'sales_team' || user?.is_sales_limited)
  ) {
    return <Navigate to="/admin/sales" replace />;
  }

  return children;
};

export default ProtectedRoute;
